import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { CheckCircle, XCircle, AlertCircle, Settings } from 'lucide-react';

/**
 * Integration test component for waitlist system
 * Only shows in development mode when ?test=waitlist is in URL
 */
const WaitlistIntegrationTest = () => {
  const [tests, setTests] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  
  // Redux state
  const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
  const currentUser = useSelector(state => state.auth.currentUser);
  const waitlistState = useSelector(state => state.waitlist);

  useEffect(() => {
    // Only show in development with test parameter
    const urlParams = new URLSearchParams(window.location.search);
    const showTest = process.env.NODE_ENV === 'development' && urlParams.get('test') === 'waitlist';
    setIsVisible(showTest);

    if (showTest) {
      runIntegrationTests();
    }

    function runIntegrationTests() {
    const testResults = [];

    // Test 1: Environment Configuration
    const waitlistEnabled = process.env.REACT_APP_WAITLIST_ENABLED === 'true';
    const adminEmails = process.env.REACT_APP_WAITLIST_ADMIN_EMAILS?.split(',') || [];
    
    testResults.push({
      name: 'Environment Configuration',
      status: adminEmails.length > 0 ? 'pass' : 'warn',
      details: `Waitlist: ${waitlistEnabled ? 'Enabled' : 'Disabled'}, Admins: ${adminEmails.length}`,
      description: 'Checks if environment variables are properly set'
    });

    // Test 2: Redux Store Integration
    const hasWaitlistSlice = !!waitlistState;
    testResults.push({
      name: 'Redux Store Integration',
      status: hasWaitlistSlice ? 'pass' : 'fail',
      details: hasWaitlistSlice ? 'Waitlist slice found in store' : 'Waitlist slice missing',
      description: 'Verifies waitlist Redux slice is properly integrated'
    });

    // Test 3: Authentication State
    testResults.push({
      name: 'Authentication State',
      status: 'info',
      details: `Authenticated: ${isAuthenticated}, User: ${currentUser?.email || 'None'}`,
      description: 'Current authentication status'
    });

    // Test 4: Route Protection Logic
    const routeProtectionWorks = typeof window !== 'undefined' && window.location.pathname;
    testResults.push({
      name: 'Route Protection',
      status: routeProtectionWorks ? 'pass' : 'warn',
      details: `Current route: ${routeProtectionWorks}`,
      description: 'Checks if routing logic is working'
    });

    // Test 5: Firebase Configuration
    const firebaseConfigured = !!process.env.REACT_APP_FIREBASE_PROJECT_ID;
    testResults.push({
      name: 'Firebase Configuration',
      status: firebaseConfigured ? 'pass' : 'fail',
      details: firebaseConfigured ? 'Firebase project ID found' : 'Firebase not configured',
      description: 'Verifies Firebase is properly configured'
    });

    // Test 6: Component Loading
    const componentsLoaded = typeof React !== 'undefined';
    testResults.push({
      name: 'Component Loading',
      status: componentsLoaded ? 'pass' : 'fail',
      details: 'React and components are loading correctly',
      description: 'Verifies all React components can load'
    });

    setTests(testResults);
    }
  }, [isAuthenticated, currentUser, waitlistState]);

  if (!isVisible) {
    return null;
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'fail':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'warn':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Settings className="w-5 h-5 text-blue-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pass':
        return 'border-green-200 bg-green-50';
      case 'fail':
        return 'border-red-200 bg-red-50';
      case 'warn':
        return 'border-yellow-200 bg-yellow-50';
      default:
        return 'border-blue-200 bg-blue-50';
    }
  };

  const passCount = tests.filter(t => t.status === 'pass').length;
  const failCount = tests.filter(t => t.status === 'fail').length;
  const warnCount = tests.filter(t => t.status === 'warn').length;

  return (
    <div className="fixed top-4 right-4 w-96 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto">
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Waitlist Integration Test
        </h3>
        <div className="flex gap-4 mt-2 text-sm">
          <span className="text-green-600">✓ {passCount} Pass</span>
          <span className="text-red-600">✗ {failCount} Fail</span>
          <span className="text-yellow-600">⚠ {warnCount} Warn</span>
        </div>
      </div>
      
      <div className="p-4 space-y-3">
        {tests.map((test, index) => (
          <div
            key={index}
            className={`p-3 rounded-lg border ${getStatusColor(test.status)}`}
          >
            <div className="flex items-start gap-3">
              {getStatusIcon(test.status)}
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 text-sm">
                  {test.name}
                </div>
                <div className="text-xs text-gray-600 mt-1">
                  {test.description}
                </div>
                <div className="text-xs text-gray-500 mt-1 font-mono">
                  {test.details}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="text-xs text-gray-500">
          Add <code>?test=waitlist</code> to URL to show this panel in development.
          <br />
          Remove parameter to hide.
        </div>
      </div>
    </div>
  );
};

export default WaitlistIntegrationTest;
